import React, { useMemo, useState } from 'react';
import { FormField, Dropdown, SecondaryButton, ActionButton } from '@/shared/components/ui';
import { useUIStore } from '@/stores/uiStore';
import { DEPARTMENT_OPTIONS, POSITION_OPTIONS, EMPLOYMENT_TYPE_OPTIONS } from '@/shared/data';
import { useValidation } from '@/shared/hooks/useValidation';

/**
 * EditEmployeeModal
 * Minimal modal to edit employee basic credentials: full name, email, department, position, employment type.
 * Persists to localStorage temporarily under key `csas.employeeEdits` (per employeeId) for demo purposes.
 */
export const EditEmployeeModal = ({ onClose, data = {} }) => {
  const { employeeId, initial = {} } = data;
  const { showSuccessToast } = useUIStore();

  const [form, setForm] = useState({
    fullName: initial.name || '',
    email: initial.email || '',
    department: initial.department || '',
    position: initial.position || '',
    employmentType: initial.employmentType || 'full-time',
  });

  const {
    errors,
    validateFieldOnBlur,
    validateForm,
  } = useValidation();

  const departmentOptions = useMemo(() => [{ value: '', label: 'Select Department' }, ...DEPARTMENT_OPTIONS.map(o => ({ value: o.key, label: o.label }))], []);
  const positionOptions = useMemo(() => [{ value: '', label: 'Select Position' }, ...POSITION_OPTIONS.map(o => ({ value: o.key, label: o.label }))], []);

  const onChange = (field) => (e) => setForm(s => ({ ...s, [field]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const { isValid } = validateForm({ fullName: form.fullName, email: form.email });
    if (!isValid) return;
    // Persist minimal demo edits to localStorage (overlay), keyed by employeeId
    try {
      const key = 'csas.employeeEdits';
      const raw = localStorage.getItem(key);
      const map = raw ? JSON.parse(raw) : {};
      map[employeeId] = {
        name: form.fullName,
        email: form.email,
        department: form.department,
        position: form.position,
        employmentType: form.employmentType,
        updatedAt: new Date().toISOString(),
      };
      localStorage.setItem(key, JSON.stringify(map));
    } catch {}
    showSuccessToast('Employee updated', 'Employee details were updated successfully.');
    onClose();
  };

  return (
    <div className="p-1">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Employee</h3>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          label="Full name"
          name="fullName"
          value={form.fullName}
          onChange={onChange('fullName')}
          onBlur={() => validateFieldOnBlur('fullName', form.fullName)}
          error={errors.fullName}
          placeholder="e.g., Juan Dela Cruz"
          required
        />
        <FormField
          label="Email"
          name="email"
          type="email"
          value={form.email}
          onChange={onChange('email')}
          onBlur={() => validateFieldOnBlur('email', form.email)}
          error={errors.email}
          placeholder="name@example.com"
          required
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
          <Dropdown name="department" value={form.department} onChange={onChange('department')} options={departmentOptions} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
          <Dropdown name="position" value={form.position} onChange={onChange('position')} options={positionOptions} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Employment type</label>
          <Dropdown name="employmentType" value={form.employmentType} onChange={onChange('employmentType')} options={EMPLOYMENT_TYPE_OPTIONS} />
        </div>

        <div className="md:col-span-2 flex gap-2 justify-end mt-2">
          <SecondaryButton type="button" onClick={onClose}>Cancel</SecondaryButton>
          <ActionButton type="submit" variant="tableActionbutton">Save changes</ActionButton>
        </div>
      </form>
    </div>
  );
};

export default EditEmployeeModal;
